import type { ConversionReport, FretRules, HealthResponse, JobRecord } from './types';

const DEFAULT_API_BASE = import.meta.env.VITE_API_BASE ?? '';

export function normalizeApiBase(apiBase: string): string {
  return apiBase.replace(/\/$/, '');
}

export function endpoint(path: string, apiBase = DEFAULT_API_BASE): string {
  const base = normalizeApiBase(apiBase);
  return `${base}${path}`;
}

async function jsonRequest<T>(path: string, apiBase: string, init?: RequestInit): Promise<T> {
  const response = await fetch(endpoint(path, apiBase), init);
  if (!response.ok) {
    const body = await response.text();
    throw new Error(body || `${response.status} ${response.statusText}`);
  }
  return (await response.json()) as T;
}

export function getHealth(apiBase: string): Promise<HealthResponse> {
  return jsonRequest<HealthResponse>('/health', apiBase);
}

export function getDefaultRules(apiBase: string): Promise<FretRules> {
  return jsonRequest<FretRules>('/api/v1/default-rules', apiBase);
}

export function listJobs(apiBase: string): Promise<{ jobs: JobRecord[] }> {
  return jsonRequest<{ jobs: JobRecord[] }>('/api/v1/jobs', apiBase);
}

export function getJob(apiBase: string, jobId: string): Promise<JobRecord> {
  return jsonRequest<JobRecord>(`/api/v1/jobs/${jobId}`, apiBase);
}

export function getReport(apiBase: string, jobId: string): Promise<ConversionReport> {
  return jsonRequest<ConversionReport>(`/api/v1/jobs/${jobId}/report`, apiBase);
}

export function submitAnalyze(apiBase: string, file: File): Promise<JobRecord> {
  const body = new FormData();
  body.append('file', file);
  return jsonRequest<JobRecord>('/api/v1/analyze', apiBase, { method: 'POST', body });
}

export function submitConvert(apiBase: string, file: File, rules: FretRules): Promise<JobRecord> {
  const body = new FormData();
  body.append('file', file);
  body.append('rules', JSON.stringify(rules));
  return jsonRequest<JobRecord>('/api/v1/convert', apiBase, { method: 'POST', body });
}

export function submitSolve(apiBase: string, jobId: string, rules: FretRules): Promise<JobRecord> {
  const body = new FormData();
  body.append('rules', JSON.stringify(rules));
  return jsonRequest<JobRecord>(`/api/v1/jobs/${jobId}/solve`, apiBase, { method: 'POST', body });
}

export function artifactUrl(apiBase: string, url: string): string {
  return endpoint(url, apiBase);
}

export async function getTextArtifact(apiBase: string, url: string): Promise<string> {
  const response = await fetch(artifactUrl(apiBase, url));
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
  return response.text();
}
